/**
 * Leviathan Web - Tank History Page
 * Quasar Prime LLC
 * Tank assignment history and performance tracking
 */

import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { parseApiError } from '@/utils/errorHandler';
import {
  ArrowLeft,
  Truck,
  MapPin,
  Calendar,
  DollarSign,
  TrendingUp,
  Clock,
  Building2,
  type LucideIcon,
} from 'lucide-react';
import { API_URL } from '@/config/api';

interface Tank {
  id: string;
  tankNumber: string;
  name: string;
  capacity: number;
  currentLevel: number;
  totalGallonsDelivered: number;
  totalDeliveries: number;
  product: {
    id: string;
    name: string;
    sku: string;
  } | null;
  customerLocation: {
    id: string;
    name: string;
    locationCode: string | null;
    address: string | null;
    city: string | null;
    state: string | null;
    customer: {
      id: string;
      name: string;
      customerNumber: string;
    };
  } | null;
}

interface Assignment {
  id: string;
  assignedDate: string;
  unassignedDate: string | null;
  notes: string | null;
  gallonsDelivered: number;
  totalRevenue: number;
  totalCost: number;
  profit: number;
  customer: {
    id: string;
    name: string;
    customerNumber: string;
  };
  customerLocation: {
    id: string;
    name: string;
    locationCode: string | null;
    address: string | null;
    city: string | null;
    state: string | null;
  };
}

interface TankHistoryData {
  tank: Tank;
  currentAssignment: Assignment | null;
  assignments: Assignment[];
  totals: {
    totalGallonsAllTime: number;
    totalDeliveriesAllTime: number;
    averagePerDelivery: number;
  };
}

export default function TankHistory() {
  const { id } = useParams();
  const { accessToken } = useAuth();

  const [data, setData] = useState<TankHistoryData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchHistory();
  }, [id]);

  const fetchHistory = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/tanks/${id}/history`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (!response.ok) {
        const errorMessage = await parseApiError(response);
        throw new Error(errorMessage);
      }

      const result = await response.json();
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch tank history');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const calculateDuration = (start: string, end: string | null) => {
    const startDate = new Date(start);
    const endDate = end ? new Date(end) : new Date();
    const days = Math.floor((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
    if (days < 30) return `${days} days`;
    if (days < 365) return `${Math.floor(days / 30)} months`;
    return `${Math.floor(days / 365)} years, ${Math.floor((days % 365) / 30)} months`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-text-tertiary font-mono animate-pulse">Loading...</div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="text-center py-12">
        <Truck size={48} className="mx-auto mb-4 text-text-tertiary" />
        <div className="text-text-tertiary font-mono mb-2">{error || 'Tank not found'}</div>
        <Link to="/tanks" className="text-amber hover:text-amber/80 font-mono text-sm">
          Back to Tanks
        </Link>
      </div>
    );
  }

  const { tank, currentAssignment, assignments, totals } = data;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="relative">
          <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-amber via-steel to-amber" />
          <div className="pl-6">
            <Link
              to={`/tanks/${id}`}
              className="back-link mb-3"
            >
              <ArrowLeft size={14} />
              Back to Tank Details
            </Link>
            <h1
              className="text-4xl font-display font-bold text-steel tracking-wide uppercase"
              style={{ textShadow: '0 0 20px rgba(92, 140, 199, 0.6)' }}
            >
              {tank.tankNumber} History
            </h1>
            <p className="text-text-secondary text-sm font-mono mt-1">
              {tank.name} {tank.product && `• ${tank.product.name}`}
            </p>
          </div>
        </div>
      </div>

      {/* Lifetime Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard
          icon={Truck}
          label="Total Deliveries"
          value={totals.totalDeliveriesAllTime.toLocaleString()}
        />
        <StatCard
          icon={TrendingUp}
          label="Total Gallons"
          value={`${Number(totals.totalGallonsAllTime).toLocaleString()} gal`}
        />
        <StatCard
          icon={DollarSign}
          label="Avg Per Delivery"
          value={`${Math.round(totals.averagePerDelivery).toLocaleString()} gal`}
        />
        <StatCard
          icon={Clock}
          label="Assignments"
          value={assignments.length.toString()}
        />
      </div>

      {/* Current Assignment */}
      {currentAssignment && (
        <Panel title="Current Assignment">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <Building2 size={20} className="text-amber mt-1 flex-shrink-0" />
                <div>
                  <Link
                    to={`/customers/${currentAssignment.customer.id}`}
                    className="text-text-primary font-mono font-semibold hover:text-amber"
                  >
                    {currentAssignment.customer.name}
                  </Link>
                  <div className="text-text-tertiary font-mono text-sm">
                    {currentAssignment.customer.customerNumber}
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <MapPin size={20} className="text-steel mt-1 flex-shrink-0" />
                <div>
                  <Link
                    to={`/customers/${currentAssignment.customer.id}/locations/${currentAssignment.customerLocation.id}`}
                    className="text-text-primary font-mono hover:text-amber"
                  >
                    {currentAssignment.customerLocation.name}
                  </Link>
                  {currentAssignment.customerLocation.address && (
                    <div className="text-text-secondary font-mono text-sm">
                      {currentAssignment.customerLocation.address}
                      {currentAssignment.customerLocation.city && (
                        <>, {currentAssignment.customerLocation.city}</>
                      )}
                      {currentAssignment.customerLocation.state && (
                        <> {currentAssignment.customerLocation.state}</>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Calendar size={20} className="text-text-tertiary" />
                <div className="text-text-secondary font-mono text-sm">
                  Assigned: {formatDate(currentAssignment.assignedDate)} (
                  {calculateDuration(currentAssignment.assignedDate, null)})
                </div>
              </div>
            </div>

            <div className="bg-background/50 border border-panel-border p-4">
              <h4 className="text-text-tertiary text-xs uppercase tracking-widest font-mono mb-4">
                Assignment Performance
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-text-tertiary text-xs font-mono uppercase">Gallons</div>
                  <div className="text-xl font-display font-bold text-text-primary">
                    {currentAssignment.gallonsDelivered.toLocaleString()}
                  </div>
                </div>
                <div>
                  <div className="text-text-tertiary text-xs font-mono uppercase">Revenue</div>
                  <div className="text-xl font-display font-bold text-success-green">
                    {formatCurrency(currentAssignment.totalRevenue)}
                  </div>
                </div>
                <div>
                  <div className="text-text-tertiary text-xs font-mono uppercase">Cost</div>
                  <div className="text-xl font-display font-bold text-text-secondary">
                    {formatCurrency(currentAssignment.totalCost)}
                  </div>
                </div>
                <div>
                  <div className="text-text-tertiary text-xs font-mono uppercase">Profit</div>
                  <div
                    className={`text-xl font-display font-bold ${
                      currentAssignment.profit >= 0 ? 'text-success-green' : 'text-error-red'
                    }`}
                  >
                    {formatCurrency(currentAssignment.profit)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Panel>
      )}

      {!currentAssignment && (
        <Panel title="Current Assignment">
          <div className="text-center py-8">
            <MapPin size={32} className="mx-auto mb-3 text-text-tertiary" />
            <div className="text-text-tertiary font-mono text-sm">
              Tank is not currently assigned to a location
            </div>
          </div>
        </Panel>
      )}

      {/* Assignment History */}
      <Panel title={`Assignment History (${assignments.length})`}>
        {assignments.length === 0 ? (
          <div className="text-center py-8">
            <Clock size={32} className="mx-auto mb-3 text-text-tertiary" />
            <div className="text-text-tertiary font-mono text-sm">No assignment history</div>
          </div>
        ) : (
          <div className="space-y-3">
            {assignments.map((assignment) => (
              <div
                key={assignment.id}
                className={`bg-background/50 border p-4 transition-colors ${
                  !assignment.unassignedDate
                    ? 'border-amber/50'
                    : 'border-panel-border hover:border-panel-border/80'
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-text-primary font-mono font-semibold">
                        {assignment.customer.name}
                      </span>
                      {!assignment.unassignedDate && (
                        <span className="px-2 py-0.5 text-xs font-mono bg-success-green/20 text-success-green border border-success-green">
                          CURRENT
                        </span>
                      )}
                    </div>
                    <div className="text-text-secondary font-mono text-sm">
                      {assignment.customerLocation.name}
                      {assignment.customerLocation.locationCode && (
                        <span className="text-text-tertiary">
                          {' '}
                          ({assignment.customerLocation.locationCode})
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="text-right">
                    <div className="text-text-secondary font-mono text-sm">
                      {formatDate(assignment.assignedDate)}
                      {assignment.unassignedDate && (
                        <> → {formatDate(assignment.unassignedDate)}</>
                      )}
                    </div>
                    <div className="text-text-tertiary font-mono text-xs">
                      {calculateDuration(assignment.assignedDate, assignment.unassignedDate)}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-4 pt-3 border-t border-panel-border">
                  <div>
                    <div className="text-text-tertiary text-xs font-mono uppercase">Gallons</div>
                    <div className="text-text-primary font-mono">
                      {assignment.gallonsDelivered.toLocaleString()}
                    </div>
                  </div>
                  <div>
                    <div className="text-text-tertiary text-xs font-mono uppercase">Revenue</div>
                    <div className="text-text-primary font-mono">
                      {formatCurrency(assignment.totalRevenue)}
                    </div>
                  </div>
                  <div>
                    <div className="text-text-tertiary text-xs font-mono uppercase">Cost</div>
                    <div className="text-text-primary font-mono">
                      {formatCurrency(assignment.totalCost)}
                    </div>
                  </div>
                  <div>
                    <div className="text-text-tertiary text-xs font-mono uppercase">Profit</div>
                    <div
                      className={`font-mono ${
                        assignment.profit >= 0 ? 'text-success-green' : 'text-error-red'
                      }`}
                    >
                      {formatCurrency(assignment.profit)}
                    </div>
                  </div>
                </div>

                {assignment.notes && (
                  <div className="mt-3 pt-3 border-t border-panel-border">
                    <div className="text-text-tertiary font-mono text-sm italic">
                      {assignment.notes}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}

function StatCard({
  icon: Icon,
  label,
  value,
}: {
  icon: LucideIcon;
  label: string;
  value: string;
}) {
  return (
    <div className="relative bg-panel-dark border border-panel-border p-4">
      <div className="absolute top-0 left-0 w-2 h-2 border-l border-t border-amber" />
      <div className="absolute top-0 right-0 w-2 h-2 border-r border-t border-amber" />
      <div className="absolute bottom-0 left-0 w-2 h-2 border-l border-b border-amber" />
      <div className="absolute bottom-0 right-0 w-2 h-2 border-r border-b border-amber" />

      <div className="flex items-center gap-3">
        <Icon size={20} className="text-amber" />
        <div>
          <div className="text-text-tertiary text-xs uppercase tracking-widest font-mono">
            {label}
          </div>
          <div className="text-xl font-display font-bold text-text-primary">{value}</div>
        </div>
      </div>
    </div>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="relative">
      <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
      <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
      <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
      <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

      <div className="bg-panel-dark border border-panel-border p-6">
        <h3 className="text-lg font-display text-text-primary uppercase tracking-wide mb-4 pb-3 border-b border-amber">
          {title}
        </h3>
        {children}
      </div>
    </div>
  );
}
